import React, { createContext, useContext, useState, ReactNode } from "react";

interface CarTranslation {
  price: string;
  description: string;
  driveType: string;
  exteriorColor: string;
  interiorColor: string;
}

interface Car {
  id: number;
  translations: {
    en: CarTranslation;
    fr: CarTranslation;
    de: CarTranslation;
  };
  title: string;
  subtitle: string;
  year: string;
  power: string;
  mileage: string;
  engine: string;
  bodyType: string;
  transmission: string;
  maxTorque: string;
  topSpeed: string;
  acceleration: string;
  specifications: string;
  limitedTo: string;
  imageUrl: string;
  photoGallery: string[];
  videoUrl: string;
  brochureUrl: string;
  soundUrl: string;
  linkAutoScout24: string;
}

interface CarContextType {
  cars: Car[];
  setCars: React.Dispatch<React.SetStateAction<Car[]>>;
}

const CarContext = createContext<CarContextType | undefined>(undefined);

export const useCarContext = () => {
  const context = useContext(CarContext);
  if (!context) {
    throw new Error("useCarContext must be used within a CarProvider"); // Renvoie une erreur si le contexte n'est pas disponible
  }
  return context;
};

export const CarProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [cars, setCars] = useState<Car[]>([
    {
      id: 1,
      translations: {
        en: {
          driveType: "Rear Wheel Drive",
          exteriorColor: "Black",
          interiorColor: "Black",
          price: "€ 4 200 000 ",
          description:
            "  RMG, specialized in luxury car sales, is proud to offer a vehicle with unique technology on the market, directly derived from Formula 1. A true masterpiece of engineering and design, the Valkyrie offers a rare experience with unparalleled circuit performance while being road-legal. Chassis, aerodynamics, and innovation define this creation with aggressive yet elegant lines. Produced in only 150 units worldwide, this performance monster was previously owned by a legendary Formula 1 world champion. As a final touch, the car features the pilot’s racing number, and Aston Martin has specially created two plaques in his honor on the car’s entrances. Don't miss the opportunity to own a unique Valkyrie. Imagine yourself behind the wheel of this beast from Formula 1, configured by a legendary champion. All documents attesting to its authenticity are available. We would be delighted to answer all your questions and look forward to offering you a unique acquisition experience.",
        },
        fr: {
          driveType: "Propulsion",
          exteriorColor: "Noir",
          interiorColor: "Noir",
          price: "4 200 000 €",
          description:
            "RMG, spécialisé dans la vente de véhicules de luxe, est fier de vous proposer un modèle à la technologie unique sur le marché, issu directement de la Formule 1. Véritable chef-d'œuvre d'ingénierie et de design, la Valkyrie offre une expérience rare avec des performances inégalables sur circuit mais homologuées pour la route. Châssis, aérodynamique, innovation sont les maîtres mots pour définir cette création aux lignes agressives et élégantes à la fois. Produite à seulement 150 exemplaires dans le monde, ce monstre de performance que nous vous offrons possède la particularité exceptionnelle d'avoir été la propriété d'un immense champion du monde de FORMULE 1. Pour couronner le tout, la voiture porte son numéro de pilote et Aston Martin a spécialement créé deux plaques à son effigie sur les entrées de cette œuvre d'art. Donnez-vous l'opportunité d'acquérir une Valkyrie unique. Imaginez-vous au volant de ce monstre issu de la FORMULE 1 et configuré par un immense champion de la catégorie. Tous les documents attestant de son authenticité sont disponibles. Nous serons ravis de répondre à toutes vos questions et nous attendons avec impatience de vous faire découvrir une expérience d'acquisition unique.",
        },
        de: {
          driveType: "Hinterradantrieb",
          exteriorColor: "Schwarz",
          interiorColor: "Schwarz",
          price: "3 944 480 CHF",
          description:
            "RMG, spezialisiert auf den Verkauf von Luxusfahrzeugen, ist stolz darauf, Ihnen ein Modell mit einzigartiger Technologie auf dem Markt anzubieten, das direkt aus der Formel 1 stammt. Ein wahres Meisterwerk der Ingenieurskunst und des Designs, die Valkyrie bietet eine seltene Erfahrung mit unvergleichlicher Leistung auf der Rennstrecke, ist aber auch für die Straße zugelassen. Chassis, Aerodynamik und Innovation definieren dieses Fahrzeug mit aggressiven und dennoch eleganten Linien. Weltweit auf nur 150 Exemplare limitiert, wurde dieses Hochleistungsfahrzeug von einem legendären Formel-1-Weltmeister konfiguriert und besessen. Als besonderes Highlight trägt das Auto die Rennnummer des Piloten, und Aston Martin hat speziell zwei Plaketten zu seinen Ehren an den Einstiegen angebracht. Nutzen Sie die Gelegenheit, eine einzigartige Valkyrie zu erwerben. Stellen Sie sich vor, Sie sitzen hinter dem Steuer dieses Biestes aus der Formel 1, das von einem legendären Champion konfiguriert wurde. Alle Authentizitätsdokumente sind verfügbar. Wir freuen uns, Ihre Fragen zu beantworten und Ihnen ein einzigartiges Kauferlebnis zu bieten.",
        },
      },
      title: "Aston Martin",
      subtitle: "Valkyrie",
      year: "2024",
      power: "1,160 Horsepower",
      mileage: "80 KM",
      engine: "6.5L V12 Hybrid Engine",
      bodyType: "Hypercar",
      transmission: "Automatic",
      maxTorque: "900 Nm",
      topSpeed: "350 Km/h",
      acceleration: "2.5s Approx",
      specifications: "Europe",
      limitedTo: "150 Units",
      imageUrl: "/images/cars/VALKYRIE/Valkyrie14.jpeg",
      photoGallery: [
        "/images/cars/VALKYRIE/Valkyrie01.jpeg",
        "/images/cars/VALKYRIE/Valkyrie02.jpeg",
        "/images/cars/VALKYRIE/Valkyrie03.jpeg",
        "/images/cars/VALKYRIE/Valkyrie04.jpeg",
        "/images/cars/VALKYRIE/Valkyrie05.jpeg",
        "/images/cars/VALKYRIE/Valkyrie06.jpeg",
        "/images/cars/VALKYRIE/Valkyrie07.jpeg",
        "/images/cars/VALKYRIE/Valkyrie08.jpeg",
        "/images/cars/VALKYRIE/Valkyrie09.jpeg",
        "/images/cars/VALKYRIE/Valkyrie10.jpeg",
        "/images/cars/VALKYRIE/Valkyrie11.jpeg",
        "/images/cars/VALKYRIE/Valkyrie12.jpeg",
        "/images/cars/VALKYRIE/Valkyrie13.jpeg",
        "/images/cars/VALKYRIE/Valkyrie14.jpeg",
      ],
      videoUrl: "https://www.youtube.com/embed/example",
      brochureUrl: "",
      soundUrl: "/sounds/AC_cobra.mp3",
      linkAutoScout24: "https://www.autoscout24.ch/fr/d/aston-martin-12148599",
    },
    {
      id: 2,
      translations: {
        en: {
          driveType: "Rear Wheel Drive",
          exteriorColor: "Rosso Corsa",
          interiorColor: "Black Alcantara",
          price: "On Request",
          description:
            "The Ferrari Monza SP1 is an exclusive single-seater roadster inspired by Ferrari’s legendary barchettas of the 1950s. Designed for pure driving pleasure, it combines an open-top design with a powerful 6.5L V12 engine, delivering an unmatched experience. Limited to only 499 units worldwide, the SP1 is a rare masterpiece of engineering and aerodynamics, offering thrilling performance both on road and track.",
        },
        fr: {
          driveType: "Propulsion",
          exteriorColor: "Rosso Corsa",
          interiorColor: "Alcantara Noir",
          price: "Sur demande",
          description:
            "La Ferrari Monza SP1 est un roadster monoplace exclusif inspiré des barchettas légendaires des années 50. Conçue pour un plaisir de conduite ultime, elle associe un design ouvert à un puissant moteur V12 6.5L, offrant une expérience unique. Limitée à seulement 499 exemplaires dans le monde, la SP1 est un chef-d'œuvre d'ingénierie et d'aérodynamique, procurant des sensations inégalées sur route comme sur circuit.",
        },
        de: {
          driveType: "Hinterradantrieb",
          exteriorColor: "Rosso Corsa",
          interiorColor: "Schwarzes Alcantara",
          price: "Auf Anfrage",
          description:
            "Der Ferrari Monza SP1 ist ein exklusiver einsitziger Roadster, inspiriert von den legendären Barchettas der 1950er Jahre. Entwickelt für pures Fahrvergnügen, kombiniert er ein offenes Design mit einem leistungsstarken 6,5L V12-Motor und bietet ein unvergleichliches Erlebnis. Weltweit auf nur 499 Exemplare limitiert, ist der SP1 ein Meisterwerk der Ingenieurskunst und Aerodynamik, das sowohl auf der Straße als auch auf der Rennstrecke begeistert.",
        },
      },
      title: "Ferrari",
      subtitle: "Monza SP1",
      year: "2020",
      power: "810 Horsepower",
      mileage: "150 KM",
      engine: "6.5L V12 Naturally Aspirated",
      bodyType: "Speedster",
      transmission: "7-Speed Dual-Clutch Automatic",
      maxTorque: "719 Nm",
      topSpeed: "300 Km/h",
      acceleration: "2.9s Approx",
      specifications: "Europe",
      limitedTo: "499 Units",
      imageUrl: "/images/cars/SP1/SP103.jpeg",
      photoGallery: [
        "/images/cars/SP1/SP101.jpeg",
        "/images/cars/SP1/SP102.jpeg",
        "/images/cars/SP1/SP103.jpeg",
        "/images/cars/SP1/SP104.jpeg",
        "/images/cars/SP1/SP105.jpeg",
        "/images/cars/SP1/SP106.jpeg",
        "/images/cars/SP1/SP107.jpeg",
        "/images/cars/SP1/SP108.jpeg",
        "/images/cars/SP1/SP109.jpeg",
        "/images/cars/SP1/SP110.jpeg",
        "/images/cars/SP1/SP111.jpeg",
        "/images/cars/SP1/SP112.jpeg",
        "/images/cars/SP1/SP113.jpeg",
        "/images/cars/SP1/SP114.jpeg",
        "/images/cars/SP1/SP115.jpeg",
        "/images/cars/SP1/SP116.jpeg",
      ],
      videoUrl: "https://www.youtube.com/embed/example",
      brochureUrl: "",
      soundUrl: "/sounds/AC_cobra.mp3",
      linkAutoScout24: "https://www.autoscout24.ch/fr/d/aston-martin-12148599",
    },
    {
      id: 3,
      translations: {
        en: {
          driveType: "All Wheel Drive",
          price: "CHF119,900",
          description:
            "The ENZO Competition M combines cutting-edge technology with extraordinary driving dynamics.",
          exteriorColor: "White",
          interiorColor: "Red",
        },
        fr: {
          driveType: "Transmission intégrale",
          price: "119 900 CHF",
          description:
            "Auto Idealy, spécialisé dans la vente de véhicules de luxe, est fier de vous proposer un modèle à la technologie unique sur le marché, issu directement de la Formule 1 Véritable chef d œuvre d ingénierie et de design, la Valkyrie offre une expérience rare avec des performances inégalables sur circuit mais homologuées pour la route. Châssis, aérodynamique, innovation sont les maîtres mots pour définir cette création aux lignes agressives et élégantes à la fois. Produite à seulement 150 exemplaires dans le monde, ce monstre de performance que nous vous offrons possède la particularité exceptionnelle d'avoir été la propriété d'un immense champion du monde de FORMULE 1. Pour couronner le tout, la voiture porte son numéro de pilote et Aston Martin à spécialement créé deux plaques à son effigie sur les entrées de cette œuvre d'art. Donnez-vous l'opportunité d'acquérir une valkyrie unique. Imaginez vous au volant de ce monstre issue de la FORMULE 1 et configuré par un immense champion de la catégorie. Tous les documents attestant de son authenticité sont disponibles. Nous serons ravis de répondre à toutes vos questions et nous attendons avec impatience de vous faire découvrir une expérience d'acquisition unique.",
          exteriorColor: "Blanc",
          interiorColor: "Rouge",
        },
        de: {
          driveType: "Allradantrieb",
          price: "119.900 CHF",
          description:
            "Der BMW M3 Competition M kombiniert modernste Technologie mit außergewöhnlicher Fahrdynamik.",
          exteriorColor: "Weiß",
          interiorColor: "Rot",
        },
      },
      title: "FERRARI",
      subtitle: "ENZO",
      year: "2023",
      power: "510 Ch",
      mileage: "1,200 KM",

      engine: "3.0L Twin-Turbo Inline-6",
      bodyType: "Sedan",
      transmission: "Automatic",
      maxTorque: "650 Nm",
      topSpeed: "290 Km/h",
      acceleration: "3.8s",
      specifications: "Europe",
      limitedTo: "No",
      imageUrl: "/images/cars/ENZO/Simongosselinphotography_Enzo-4.jpg",

      photoGallery: [
        "/images/cars/ENZO/Enzo-1.jpg",
        "/images/cars/ENZO/Enzo-2.jpg",
        "/images/cars/ENZO/Simongosselinphotography_Enzo-3.jpg",
        "/images/cars/ENZO/Simongosselinphotography_Enzo-4.jpg",
        "/images/cars/ENZO/Simongosselinphotography_Enzo-5.jpg",
        "/images/cars/ENZO/Simongosselinphotography_Enzo-6.jpg",
        "/images/cars/ENZO/Simongosselinphotography_Enzo-7.jpg",
        "/images/cars/ENZO/Simongosselinphotography_Enzo-8.jpg",
        "/images/cars/ENZO/Simongosselinphotography_Enzo-9.jpg",
        "/images/cars/ENZO/Simongosselinphotography_Enzo-10.jpg",
        "/images/cars/ENZO/Simongosselinphotography_Enzo-11.jpg",
        "/images/cars/ENZO/Simongosselinphotography_Enzo-12.jpg",
        "/images/cars/ENZO/Simongosselinphotography_Enzo-13.jpg",
        "/images/cars/ENZO/Simongosselinphotography_Enzo-14.jpg",
        "/images/cars/ENZO/Simongosselinphotography_Enzo-15.jpg",
        "/images/cars/ENZO/Simongosselinphotography_Enzo-16.jpg",
        "/images/cars/ENZO/Simongosselinphotography_Enzo-17.jpg",
        "/images/cars/ENZO/Simongosselinphotography_Enzo-18.jpg",
        "/images/cars/ENZO/Simongosselinphotography_Enzo-19.jpg",
        "/images/cars/ENZO/Simongosselinphotography_Enzo-20.jpg",
        "/images/cars/ENZO/Simongosselinphotography_Enzo-21.jpg",
        "/images/cars/ENZO/Simongosselinphotography_Enzo-22.jpg",
        "/images/cars/ENZO/Simongosselinphotography_Enzo-23.jpg",
        "/images/cars/ENZO/Simongosselinphotography_Enzo-24.jpg",
        "/images/cars/ENZO/Simongosselinphotography_Enzo-25.jpg",
        "/images/cars/ENZO/Simongosselinphotography_Enzo-26.jpg",
        "/images/cars/ENZO/Simongosselinphotography_Enzo-27.jpg",
        "/images/cars/ENZO/Simongosselinphotography_Enzo-28.jpg",
        "/images/cars/ENZO/Simongosselinphotography_Enzo-29.jpg",
        "/images/cars/ENZO/Simongosselinphotography_Enzo-30.jpg",
        "/images/cars/ENZO/Simongosselinphotography_Enzo-31.jpg",
        "/images/cars/ENZO/Simongosselinphotography_Enzo-32.jpg",
        "/images/cars/ENZO/Simongosselinphotography_Enzo-33.jpg",
        "/images/cars/ENZO/Simongosselinphotography_Enzo-34.jpg",
        "/images/cars/ENZO/Simongosselinphotography_Enzo-35.jpg",
        "/images/cars/ENZO/Simongosselinphotography_Enzo-36.jpg",
        "/images/cars/ENZO/Simongosselinphotography_Enzo-37.jpg",
        "/images/cars/ENZO/Simongosselinphotography_Enzo-38.jpg",
        "/images/cars/ENZO/Simongosselinphotography_Enzo-39.jpg",
        "/images/cars/ENZO/Simongosselinphotography_Enzo-40.jpg",
        "/images/cars/ENZO/Simongosselinphotography_Enzo-41.jpg",
        "/images/cars/ENZO/Simongosselinphotography_Enzo-42.jpg",
        "/images/cars/ENZO/Simongosselinphotography_Enzo-43.jpg",
        "/images/cars/ENZO/Simongosselinphotography_Enzo-44.jpg",
        "/images/cars/ENZO/Simongosselinphotography_Enzo-45.jpg",
        "/images/cars/ENZO/Simongosselinphotography_Enzo-46.jpg",
        "/images/cars/ENZO/Simongosselinphotography_Enzo-47.jpg",
        "/images/cars/ENZO/Simongosselinphotography_Enzo-48.jpg",
        "/images/cars/ENZO/Simongosselinphotography_Enzo-49.jpg",
        "/images/cars/ENZO/Simongosselinphotography_Enzo-50.jpg",
        "/images/cars/ENZO/Simongosselinphotography_Enzo-51.jpg",
        "/images/cars/ENZO/Simongosselinphotography_Enzo-52.jpg",
        "/images/cars/ENZO/Simongosselinphotography_Enzo-53.jpg",
      ],
      videoUrl: "https://www.youtube.com/embed/example",
      brochureUrl: "",
      soundUrl: "/sounds/sonval.mp3",
      linkAutoScout24: "https://www.autoscout24.ch/fr/d/aston-martin-12148599",
    },
    {
      id: 4,
      translations: {
        en: {
          driveType: "Rear Wheel Drive",
          exteriorColor: "Rosso Corsa",
          interiorColor: "Black Alcantara",
          price: "On Request",
          description:
            "The Ferrari Monza SP1 is an exclusive single-seater roadster inspired by Ferrari’s legendary barchettas of the 1950s. Designed for pure driving pleasure, it combines an open-top design with a powerful 6.5L V12 engine, delivering an unmatched experience. Limited to only 499 units worldwide, the SP1 is a rare masterpiece of engineering and aerodynamics, offering thrilling performance both on road and track.",
        },
        fr: {
          driveType: "Propulsion",
          exteriorColor: "Rosso Corsa",
          interiorColor: "Alcantara Noir",
          price: "Sur demande",
          description:
            "La Ferrari Monza SP1 est un roadster monoplace exclusif inspiré des barchettas légendaires des années 50. Conçue pour un plaisir de conduite ultime, elle associe un design ouvert à un puissant moteur V12 6.5L, offrant une expérience unique. Limitée à seulement 499 exemplaires dans le monde, la SP1 est un chef-d'œuvre d'ingénierie et d'aérodynamique, procurant des sensations inégalées sur route comme sur circuit.",
        },
        de: {
          driveType: "Hinterradantrieb",
          exteriorColor: "Rosso Corsa",
          interiorColor: "Schwarzes Alcantara",
          price: "Auf Anfrage",
          description:
            "Der Ferrari Monza SP1 ist ein exklusiver einsitziger Roadster, inspiriert von den legendären Barchettas der 1950er Jahre. Entwickelt für pures Fahrvergnügen, kombiniert er ein offenes Design mit einem leistungsstarken 6,5L V12-Motor und bietet ein unvergleichliches Erlebnis. Weltweit auf nur 499 Exemplare limitiert, ist der SP1 ein Meisterwerk der Ingenieurskunst und Aerodynamik, das sowohl auf der Straße als auch auf der Rennstrecke begeistert.",
        },
      },
      title: "Ferrari",
      subtitle: "Monza SP1",
      year: "2020",
      power: "810 Horsepower",
      mileage: "150 KM",
      engine: "6.5L V12 Naturally Aspirated",
      bodyType: "Speedster",
      transmission: "7-Speed Dual-Clutch Automatic",
      maxTorque: "719 Nm",
      topSpeed: "300 Km/h",
      acceleration: "2.9s Approx",
      specifications: "Europe",
      limitedTo: "499 Units",
      imageUrl: "/images/cars/SP1/SP102.jpeg",
      photoGallery: [
        "/images/cars/SP1/SP102.jpeg",
        "/images/cars/SP1/SP103.jpeg",
        "/images/cars/SP1/SP104.jpeg",
        "/images/cars/SP1/SP105.jpeg",
        "/images/cars/SP1/SP106.jpeg",
        "/images/cars/SP1/SP107.jpeg",
        "/images/cars/SP1/SP108.jpeg",
        "/images/cars/SP1/SP109.jpeg",
        "/images/cars/SP1/SP110.jpeg",
        "/images/cars/SP1/SP111.jpeg",
        "/images/cars/SP1/SP112.jpeg",
        "/images/cars/SP1/SP113.jpeg",
        "/images/cars/SP1/SP114.jpeg",
        "/images/cars/SP1/SP115.jpeg",
        "/images/cars/SP1/SP116.jpeg",
      ],
      videoUrl: "https://www.youtube.com/embed/example",
      brochureUrl: "",
      soundUrl: "",
      linkAutoScout24: "https://www.autoscout24.ch/fr/d/aston-martin-12148599",
    },
    {
      id: 5,
      translations: {
        en: {
          driveType: "Rear Wheel Drive",
          exteriorColor: "Rosso Corsa",
          interiorColor: "Black Alcantara",
          price: "On Request",
          description:
            "The Ferrari Monza SP1 is an exclusive single-seater roadster inspired by Ferrari’s legendary barchettas of the 1950s. Designed for pure driving pleasure, it combines an open-top design with a powerful 6.5L V12 engine, delivering an unmatched experience. Limited to only 499 units worldwide, the SP1 is a rare masterpiece of engineering and aerodynamics, offering thrilling performance both on road and track.",
        },
        fr: {
          driveType: "Propulsion",
          exteriorColor: "Rosso Corsa",
          interiorColor: "Alcantara Noir",
          price: "Sur demande",
          description:
            "La Ferrari Monza SP1 est un roadster monoplace exclusif inspiré des barchettas légendaires des années 50. Conçue pour un plaisir de conduite ultime, elle associe un design ouvert à un puissant moteur V12 6.5L, offrant une expérience unique. Limitée à seulement 499 exemplaires dans le monde, la SP1 est un chef-d'œuvre d'ingénierie et d'aérodynamique, procurant des sensations inégalées sur route comme sur circuit.",
        },
        de: {
          driveType: "Hinterradantrieb",
          exteriorColor: "Rosso Corsa",
          interiorColor: "Schwarzes Alcantara",
          price: "Auf Anfrage",
          description:
            "Der Ferrari Monza SP1 ist ein exklusiver einsitziger Roadster, inspiriert von den legendären Barchettas der 1950er Jahre. Entwickelt für pures Fahrvergnügen, kombiniert er ein offenes Design mit einem leistungsstarken 6,5L V12-Motor und bietet ein unvergleichliches Erlebnis. Weltweit auf nur 499 Exemplare limitiert, ist der SP1 ein Meisterwerk der Ingenieurskunst und Aerodynamik, das sowohl auf der Straße als auch auf der Rennstrecke begeistert.",
        },
      },
      title: "Ferrari",
      subtitle: "Monza SP1",
      year: "2020",
      power: "810 Horsepower",
      mileage: "150 KM",
      engine: "6.5L V12 Naturally Aspirated",
      bodyType: "Speedster",
      transmission: "7-Speed Dual-Clutch Automatic",
      maxTorque: "719 Nm",
      topSpeed: "300 Km/h",
      acceleration: "2.9s Approx",
      specifications: "Europe",
      limitedTo: "499 Units",
      imageUrl: "/images/cars/SP1/SP103.jpeg",
      photoGallery: [
        "/images/cars/SP1/SP101.jpeg",
        "/images/cars/SP1/SP102.jpeg",
        "/images/cars/SP1/SP103.jpeg",
        "/images/cars/SP1/SP104.jpeg",
        "/images/cars/SP1/SP105.jpeg",
        "/images/cars/SP1/SP106.jpeg",
        "/images/cars/SP1/SP107.jpeg",
        "/images/cars/SP1/SP108.jpeg",
        "/images/cars/SP1/SP109.jpeg",
        "/images/cars/SP1/SP110.jpeg",
        "/images/cars/SP1/SP111.jpeg",
        "/images/cars/SP1/SP112.jpeg",
        "/images/cars/SP1/SP113.jpeg",
        "/images/cars/SP1/SP114.jpeg",
        "/images/cars/SP1/SP115.jpeg",
        "/images/cars/SP1/SP116.jpeg",
      ],
      videoUrl: "https://www.youtube.com/embed/example",
      brochureUrl: "",
      soundUrl: "",
      linkAutoScout24: "https://www.autoscout24.ch/fr/d/aston-martin-12148599",
    },
    {
      id: 6,
      translations: {
        en: {
          driveType: "Rear Wheel Drive",
          exteriorColor: "Black",
          interiorColor: "Black",
          price: "€ 4 200 000 ",
          description:
            "  RMG, specialized in luxury car sales, is proud to offer a vehicle with unique technology on the market, directly derived from Formula 1. A true masterpiece of engineering and design, the Valkyrie offers a rare experience with unparalleled circuit performance while being road-legal. Chassis, aerodynamics, and innovation define this creation with aggressive yet elegant lines. Produced in only 150 units worldwide, this performance monster was previously owned by a legendary Formula 1 world champion. As a final touch, the car features the pilot’s racing number, and Aston Martin has specially created two plaques in his honor on the car’s entrances. Don't miss the opportunity to own a unique Valkyrie. Imagine yourself behind the wheel of this beast from Formula 1, configured by a legendary champion. All documents attesting to its authenticity are available. We would be delighted to answer all your questions and look forward to offering you a unique acquisition experience.",
        },
        fr: {
          driveType: "Propulsion",
          exteriorColor: "Noir",
          interiorColor: "Noir",
          price: "4 200 000 €",
          description:
            "RMG, spécialisé dans la vente de véhicules de luxe, est fier de vous proposer un modèle à la technologie unique sur le marché, issu directement de la Formule 1. Véritable chef-d'œuvre d'ingénierie et de design, la Valkyrie offre une expérience rare avec des performances inégalables sur circuit mais homologuées pour la route. Châssis, aérodynamique, innovation sont les maîtres mots pour définir cette création aux lignes agressives et élégantes à la fois. Produite à seulement 150 exemplaires dans le monde, ce monstre de performance que nous vous offrons possède la particularité exceptionnelle d'avoir été la propriété d'un immense champion du monde de FORMULE 1. Pour couronner le tout, la voiture porte son numéro de pilote et Aston Martin a spécialement créé deux plaques à son effigie sur les entrées de cette œuvre d'art. Donnez-vous l'opportunité d'acquérir une Valkyrie unique. Imaginez-vous au volant de ce monstre issu de la FORMULE 1 et configuré par un immense champion de la catégorie. Tous les documents attestant de son authenticité sont disponibles. Nous serons ravis de répondre à toutes vos questions et nous attendons avec impatience de vous faire découvrir une expérience d'acquisition unique.",
        },
        de: {
          driveType: "Hinterradantrieb",
          exteriorColor: "Schwarz",
          interiorColor: "Schwarz",
          price: "3 944 480 CHF",
          description:
            "RMG, spezialisiert auf den Verkauf von Luxusfahrzeugen, ist stolz darauf, Ihnen ein Modell mit einzigartiger Technologie auf dem Markt anzubieten, das direkt aus der Formel 1 stammt. Ein wahres Meisterwerk der Ingenieurskunst und des Designs, die Valkyrie bietet eine seltene Erfahrung mit unvergleichlicher Leistung auf der Rennstrecke, ist aber auch für die Straße zugelassen. Chassis, Aerodynamik und Innovation definieren dieses Fahrzeug mit aggressiven und dennoch eleganten Linien. Weltweit auf nur 150 Exemplare limitiert, wurde dieses Hochleistungsfahrzeug von einem legendären Formel-1-Weltmeister konfiguriert und besessen. Als besonderes Highlight trägt das Auto die Rennnummer des Piloten, und Aston Martin hat speziell zwei Plaketten zu seinen Ehren an den Einstiegen angebracht. Nutzen Sie die Gelegenheit, eine einzigartige Valkyrie zu erwerben. Stellen Sie sich vor, Sie sitzen hinter dem Steuer dieses Biestes aus der Formel 1, das von einem legendären Champion konfiguriert wurde. Alle Authentizitätsdokumente sind verfügbar. Wir freuen uns, Ihre Fragen zu beantworten und Ihnen ein einzigartiges Kauferlebnis zu bieten.",
        },
      },
      title: "Aston Martin",
      subtitle: "Valkyrie",
      year: "2024",
      power: "1,160 Horsepower",
      mileage: "80 KM",
      engine: "6.5L V12 Hybrid Engine",
      bodyType: "Hypercar",
      transmission: "Automatic",
      maxTorque: "900 Nm",
      topSpeed: "350 Km/h",
      acceleration: "2.5s Approx",
      specifications: "Europe",
      limitedTo: "150 Units",
      imageUrl: "/images/cars/VALKYRIE/Valkyrie14.jpeg",
      photoGallery: [
        "/images/cars/VALKYRIE/Valkyrie01.jpeg",
        "/images/cars/VALKYRIE/Valkyrie02.jpeg",
        "/images/cars/VALKYRIE/Valkyrie03.jpeg",
        "/images/cars/VALKYRIE/Valkyrie04.jpeg",
        "/images/cars/VALKYRIE/Valkyrie05.jpeg",
        "/images/cars/VALKYRIE/Valkyrie06.jpeg",
        "/images/cars/VALKYRIE/Valkyrie07.jpeg",
        "/images/cars/VALKYRIE/Valkyrie08.jpeg",
        "/images/cars/VALKYRIE/Valkyrie09.jpeg",
        "/images/cars/VALKYRIE/Valkyrie10.jpeg",
        "/images/cars/VALKYRIE/Valkyrie11.jpeg",
        "/images/cars/VALKYRIE/Valkyrie12.jpeg",
        "/images/cars/VALKYRIE/Valkyrie13.jpeg",
        "/images/cars/VALKYRIE/Valkyrie14.jpeg",
      ],
      videoUrl: "https://www.youtube.com/embed/example",
      brochureUrl: "",
      soundUrl: "",
      linkAutoScout24: "https://www.autoscout24.ch/fr/d/aston-martin-12148599",
    },
    {
      id: 7,
      translations: {
        en: {
          driveType: "Rear Wheel Drive",
          exteriorColor: "Rosso Corsa",
          interiorColor: "Black Alcantara",
          price: "On Request",
          description:
            "The Ferrari Monza SP1 is an exclusive single-seater roadster inspired by Ferrari’s legendary barchettas of the 1950s. Designed for pure driving pleasure, it combines an open-top design with a powerful 6.5L V12 engine, delivering an unmatched experience. Limited to only 499 units worldwide, the SP1 is a rare masterpiece of engineering and aerodynamics, offering thrilling performance both on road and track.",
        },
        fr: {
          driveType: "Propulsion",
          exteriorColor: "Rosso Corsa",
          interiorColor: "Alcantara Noir",
          price: "Sur demande",
          description:
            "La Ferrari Monza SP1 est un roadster monoplace exclusif inspiré des barchettas légendaires des années 50. Conçue pour un plaisir de conduite ultime, elle associe un design ouvert à un puissant moteur V12 6.5L, offrant une expérience unique. Limitée à seulement 499 exemplaires dans le monde, la SP1 est un chef-d'œuvre d'ingénierie et d'aérodynamique, procurant des sensations inégalées sur route comme sur circuit.",
        },
        de: {
          driveType: "Hinterradantrieb",
          exteriorColor: "Rosso Corsa",
          interiorColor: "Schwarzes Alcantara",
          price: "Auf Anfrage",
          description:
            "Der Ferrari Monza SP1 ist ein exklusiver einsitziger Roadster, inspiriert von den legendären Barchettas der 1950er Jahre. Entwickelt für pures Fahrvergnügen, kombiniert er ein offenes Design mit einem leistungsstarken 6,5L V12-Motor und bietet ein unvergleichliches Erlebnis. Weltweit auf nur 499 Exemplare limitiert, ist der SP1 ein Meisterwerk der Ingenieurskunst und Aerodynamik, das sowohl auf der Straße als auch auf der Rennstrecke begeistert.",
        },
      },
      title: "Ferrari",
      subtitle: "Monza SP1",
      year: "2020",
      power: "810 Horsepower",
      mileage: "150 KM",
      engine: "6.5L V12 Naturally Aspirated",
      bodyType: "Speedster",
      transmission: "7-Speed Dual-Clutch Automatic",
      maxTorque: "719 Nm",
      topSpeed: "300 Km/h",
      acceleration: "2.9s Approx",
      specifications: "Europe",
      limitedTo: "499 Units",
      imageUrl: "/images/cars/SP1/SP103.jpeg",
      photoGallery: [
        "/images/cars/SP1/SP101.jpeg",
        "/images/cars/SP1/SP102.jpeg",
        "/images/cars/SP1/SP103.jpeg",
        "/images/cars/SP1/SP104.jpeg",
        "/images/cars/SP1/SP105.jpeg",
        "/images/cars/SP1/SP106.jpeg",
        "/images/cars/SP1/SP107.jpeg",
        "/images/cars/SP1/SP108.jpeg",
        "/images/cars/SP1/SP109.jpeg",
        "/images/cars/SP1/SP110.jpeg",
        "/images/cars/SP1/SP111.jpeg",
        "/images/cars/SP1/SP112.jpeg",
        "/images/cars/SP1/SP113.jpeg",
        "/images/cars/SP1/SP114.jpeg",
        "/images/cars/SP1/SP115.jpeg",
        "/images/cars/SP1/SP116.jpeg",
      ],
      videoUrl: "https://www.youtube.com/embed/example",
      brochureUrl: "",
      soundUrl: "",
      linkAutoScout24: "https://www.autoscout24.ch/fr/d/aston-martin-12148599",
    },
    {
      id: 8,
      translations: {
        en: {
          driveType: "All Wheel Drive",
          price: "CHF119,900",
          description:
            "The BMW M3 Competition M combines cutting-edge technology with extraordinary driving dynamics.",
          exteriorColor: "White",
          interiorColor: "Red",
        },
        fr: {
          driveType: "Transmission intégrale",
          price: "119 900 CHF",
          description:
            "Auto Idealy, spécialisé dans la vente de véhicules de luxe, est fier de vous proposer un modèle à la technologie unique sur le marché, issu directement de la Formule 1 Véritable chef d œuvre d ingénierie et de design, la Valkyrie offre une expérience rare avec des performances inégalables sur circuit mais homologuées pour la route. Châssis, aérodynamique, innovation sont les maîtres mots pour définir cette création aux lignes agressives et élégantes à la fois. Produite à seulement 150 exemplaires dans le monde, ce monstre de performance que nous vous offrons possède la particularité exceptionnelle d'avoir été la propriété d'un immense champion du monde de FORMULE 1. Pour couronner le tout, la voiture porte son numéro de pilote et Aston Martin à spécialement créé deux plaques à son effigie sur les entrées de cette œuvre d'art. Donnez-vous l'opportunité d'acquérir une valkyrie unique. Imaginez vous au volant de ce monstre issue de la FORMULE 1 et configuré par un immense champion de la catégorie. Tous les documents attestant de son authenticité sont disponibles. Nous serons ravis de répondre à toutes vos questions et nous attendons avec impatience de vous faire découvrir une expérience d'acquisition unique.",
          exteriorColor: "Blanc",
          interiorColor: "Rouge",
        },
        de: {
          driveType: "Allradantrieb",
          price: "119.900 CHF",
          description:
            "Der BMW M3 Competition M kombiniert modernste Technologie mit außergewöhnlicher Fahrdynamik.",
          exteriorColor: "Weiß",
          interiorColor: "Rot",
        },
      },
      title: "BMW M3",
      subtitle: "Competition M",
      year: "2023",
      power: "510 Ch",
      mileage: "1,200 KM",

      engine: "3.0L Twin-Turbo Inline-6",
      bodyType: "Sedan",
      transmission: "Automatic",
      maxTorque: "650 Nm",
      topSpeed: "290 Km/h",
      acceleration: "3.8s",
      specifications: "Europe",
      limitedTo: "No",
      imageUrl: "/images/cars/cars7.webp",
      photoGallery: [
        "/images/cars/cars2.webp",
        "/images/cars/cars1.webp",
        "/images/cars/cars3.webp",
      ],
      videoUrl: "https://www.youtube.com/embed/example",
      brochureUrl: "",
      soundUrl: "",
      linkAutoScout24: "https://www.autoscout24.ch/fr/d/aston-martin-12148599",
    },
  ]);
  return (
    <CarContext.Provider value={{ cars, setCars }}>
      {children}
    </CarContext.Provider>
  );
};
